import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { AccessibilityToolbar } from '@/components/AccessibilityToolbar';
import { 
  ArrowLeft, 
  Accessibility, 
  Type, 
  Contrast, 
  Eye, 
  Keyboard, 
  Volume2,
  Shield,
  CheckCircle,
  Info
} from 'lucide-react';

export default function AccessibilityPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { settings } = useAccessibility();
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const isRTL = i18n.language === 'he';

  const accessibilityFeatures = [
    {
      icon: Type,
      title: t('accessibility.features.fontSize.title', 'גודל גופן מותאם'),
      description: t('accessibility.features.fontSize.description', 'הגדל את הטקסט עד 200% לקריאה נוחה יותר'),
      enabled: settings.fontSize !== 'normal'
    },
    {
      icon: Contrast,
      title: t('accessibility.features.contrast.title', 'ניגודיות גבוהה'),
      description: t('accessibility.features.contrast.description', 'מצב ניגודיות גבוהה לראייה טובה יותר'),
      enabled: settings.highContrast
    },
    {
      icon: Eye,
      title: t('accessibility.features.motion.title', 'הפחתת תנועה'),
      description: t('accessibility.features.motion.description', 'הפחת אנימציות ותנועות למשתמשים רגישים'),
      enabled: settings.reducedMotion
    },
    {
      icon: Volume2,
      title: t('accessibility.features.screenReader.title', 'תמיכה בקוראי מסך'),
      description: t('accessibility.features.screenReader.description', 'מותאם לקוראי מסך כמו NVDA, JAWS ו-VoiceOver'),
      enabled: settings.screenReaderMode
    },
    {
      icon: Keyboard,
      title: t('accessibility.features.keyboard.title', 'ניווט מקלדת'),
      description: t('accessibility.features.keyboard.description', 'ניווט מלא באמצעות מקלדת בלבד'),
      enabled: settings.keyboardNavigation
    }
  ];

  const complianceStandards = [
    {
      title: t('accessibility.standards.israeli.title', 'התקן הישראלי'),
      description: t('accessibility.standards.israeli.description', 'ת"י 5568 חלק 1 (2018) - נגישות תכנים באינטרנט'),
      badge: 'ת"י 5568'
    },
    {
      title: t('accessibility.standards.wcag.title', 'תקן WCAG'),
      description: t('accessibility.standards.wcag.description', 'Web Content Accessibility Guidelines (WCAG) 2.1 ברמה AA'),
      badge: 'WCAG 2.1 AA'
    },
    {
      title: t('accessibility.standards.law.title', 'חוק הישראלי'),
      description: t('accessibility.standards.law.description', 'חוק שוויון זכויות לאנשים עם מוגבלות, התשנ"ח-1998'),
      badge: 'חוק נגישות'
    }
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className={`mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {t('accessibility.page.back', 'חזרה')}
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <Accessibility className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {t('accessibility.page.title', 'נגישות')}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                {t('accessibility.page.subtitle', 'התאם את האתר לצרכים שלך')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Shield className="h-4 w-4" />
            {t('accessibility.page.compliance', 'תואם לתקן הישראלי ת"י 5568 ו-WCAG 2.1 AA')}
          </div>
        </div>

        {/* Quick Access Toolbar */}
        <Card className="mb-8 border-blue-200 dark:border-blue-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-blue-600" />
              {t('accessibility.page.quickAccess.title', 'גישה מהירה להגדרות נגישות')}
            </CardTitle>
            <CardDescription>
              {t('accessibility.page.quickAccess.description', 'פתח את תיבת הכלים לנגישות כדי להתאים את האתר לצרכים שלך')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setToolbarOpen(true)}
              className="w-full sm:w-auto"
              size="lg"
            >
              <Accessibility className="h-4 w-4 mr-2" />
              {t('accessibility.page.openToolbar', 'פתח תיבת כלים לנגישות')}
            </Button>
          </CardContent>
        </Card>

        {/* Features Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibility.page.features.title', 'תכונות נגישות זמינות')}</CardTitle>
            <CardDescription>
              {t('accessibility.page.features.description', 'האתר כולל תכונות נגישות מתקדמות המותאמות לצרכים שונים')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {accessibilityFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{feature.title}</h3>
                      {feature.enabled && (
                        <Badge variant="secondary" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {t('accessibility.enabled', 'מופעל')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Keyboard Shortcuts */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              {t('accessibility.page.shortcuts.title', 'קיצורי מקלדת')}
            </CardTitle>
            <CardDescription>
              {t('accessibility.page.shortcuts.description', 'קיצורי מקלדת לניווט מהיר באתר')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="font-medium">Alt + S</span>
                <span className="text-sm text-muted-foreground">{t('accessibility.shortcuts.skipToContent', 'דלג לתוכן')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="font-medium">Tab</span>
                <span className="text-sm text-muted-foreground">{t('accessibility.shortcuts.navigate', 'נווט בין אלמנטים')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="font-medium">Enter / Space</span>
                <span className="text-sm text-muted-foreground">{t('accessibility.shortcuts.activate', 'הפעל אלמנט')}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                <span className="font-medium">Esc</span>
                <span className="text-sm text-muted-foreground">{t('accessibility.shortcuts.close', 'סגור תפריטים')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Standards */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('accessibility.page.compliance.title', 'תאימות לתקנים')}
            </CardTitle>
            <CardDescription>
              {t('accessibility.page.compliance.description', 'האתר עומד בתקנים הבאים לנגישות')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {complianceStandards.map((standard, index) => (
                <div key={index} className="flex items-start gap-3 p-4 border rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{standard.title}</h3>
                      <Badge variant="outline">{standard.badge}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{standard.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Help and Support */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              {t('accessibility.page.help.title', 'עזרה ותמיכה')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                {t('accessibility.page.help.description', 'אם נתקלת בבעיית נגישות או זקוק לסיוע נוסף, אנא פנה אלינו:')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant="outline" onClick={() => navigate('/contact')}>
                  {t('accessibility.page.help.contact', 'צור קשר')}
                </Button>
                <Button variant="outline" onClick={() => navigate('/terms')}>
                  {t('accessibility.page.help.terms', 'הצהרת נגישות')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accessibility Toolbar */}
      <AccessibilityToolbar 
        isOpen={toolbarOpen}
        onClose={() => setToolbarOpen(false)}
      />
    </div>
  );
} 